// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { flushSync } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { FHButton, FProgressBar } from '../../../../base';
import { goalActions, treatmentPlanActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { validateSchema } from '../../../../../helpers/validation-helpers';
import { goalSchema } from '../common/utils/validationSchema';
import GoalItem from './GoalItem';
import { areUserRolesIn } from '../../../../../helpers';
import { FORTA_GRAY4, FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';
import {
  MAXIMUM_TARGET_INSTRUCTION_LENGTH,
  MINIMUM_REQUIRED_SENTENCE_LENGTH,
  Roles,
  Themes,
} from '../../../../../constants';
import { TreatmentPlanPages } from '../../constants/general';
import {
  BaselineMeasurementVersion,
  DefaultInstanceMCValues,
  DefaultPCGMeasurementUnitValueV1Goal,
  DefaultPercentageDCMValueV1Goal,
  DefaultSAGMeasurementUnitValueV1Goal,
  GOAL_DETAILS_FIELD_IDS,
  GOAL_TARGET_NAME_MAX_CHARS,
  GOAL_TARGET_NAME_MIN_CHARS,
  GoalTypes,
  initialTreatmentPlanGoal,
  MasteryCriteriaChangeTypeEnum,
  TPGoalDataCollectionMethod,
  TreatmentPlanResourceVersion,
} from '../common/constants/general';
import PlusIcon from '../../../../svgs/PlusIcon';
import { validateGoalAndGetErrors, validateInstanceMasteryCriteria } from '../common/utils/general';

import type {
  IBehaviorGoalName,
  IHypothesizedFunction,
  ITreatmentPlanGoal,
  // ITreatmentPlanGoalMeasurement,
  ITreatmentPlanGoalTarget,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';

interface GoalsContainerProps {
  type: GoalTypes;
  goalVersion: TreatmentPlanResourceVersion;
}

function GoalsContainer(props: GoalsContainerProps) {
  const { type = GoalTypes.SkillAcquisition, goalVersion } = props;

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { user } = useSelector((state: RootState) => state.auth);
  const { current: behavior, goalNames } = useSelector((state: RootState) => state.behaviors);
  const { current: treatmentPlan, list: treatmentPlans } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const {
    loading: goalsLoading,
    behaviorReductionGoals,
    skillAcquisitionGoals,
    caregiverGoals,
    validationErrors,
    originalGoals,
  } = useSelector((state: RootState) => state.goals);
  const [submittings, setSubmittings] = useState<{ [key: number]: boolean } | null>(null);
  const [targetSaving, setTargetSaving] = useState<boolean>(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [goals, setGoals] = useState<ITreatmentPlanGoal[]>([]);
  const [isBRGoalDirty, setIsBRGoalDirty] = useState<boolean>(false);
  const [isSAGoalDirty, setIsSAGoalDirty] = useState<boolean>(false);
  const [isPCGoalDirty, setIsPCGoalDirty] = useState<boolean>(false);
  const goalsContainerRef = useRef<HTMLDListElement | null>(null);
  const goalsRef = useRef(goals);
  const [activeSkillAcquisitionGoals, setActiveSkillAcquisitionGoals] = useState<
    ITreatmentPlanGoal[]
  >([]);
  const [activeCaregiverGoals, setActiveCaregiverGoals] = useState<ITreatmentPlanGoal[]>([]);
  const [hasNewGoal, setHasNewGoal] = useState<boolean>(false);

  useEffect(() => {
    if (!type) return;

    switch (type) {
      case GoalTypes.BehaviorReduction:
        setGoals(behaviorReductionGoals || []);
        break;
      case GoalTypes.SkillAcquisition:
        setGoals(skillAcquisitionGoals || []);
        break;
      case GoalTypes.Caregiver:
        setGoals(caregiverGoals || []);
        break;
      default:
        setGoals([]);
        break;
    }

    const filteredBRGoals =
      behaviorReductionGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [];
    setIsBRGoalDirty(
      filteredBRGoals.some((goal) => goal?.dirty || goal.targets?.some((target) => target?.dirty)),
    );

    const filteredSAGoals =
      skillAcquisitionGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [];

    const filteredPCGoals =
      caregiverGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [];

    setIsSAGoalDirty(
      filteredSAGoals.some((goal) => goal?.dirty || goal.targets?.some((target) => target?.dirty)),
    );
    setIsPCGoalDirty(
      filteredPCGoals.some((goal) => goal?.dirty || goal.targets?.some((target) => target?.dirty)),
    );

    setActiveSkillAcquisitionGoals(filteredSAGoals);
    setActiveCaregiverGoals(filteredPCGoals);
  }, [type, behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  useEffect(() => {
    goalsRef.current = goals;

    setHasNewGoal(goals.some((goal: ITreatmentPlanGoal) => !Object.hasOwn(goal, 'id')));
    setShowScrollToTop(goals.some((goal) => goal.expanded));
  }, [goals]);

  useEffect(() => {
    if (!treatmentPlan || !validationErrors || Object.keys(validationErrors).length === 0) {
      let count = 0;
      if (isBRGoalDirty) {
        count += 1;
      }
      if (isPCGoalDirty || !activeCaregiverGoals.length) {
        count += 1;
      }
      if (isSAGoalDirty || !activeSkillAcquisitionGoals.length) {
        count += 1;
      }
      dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.Goals]: count }));
      return;
    }

    let errorCount = 0;
    Object.keys(validationErrors).forEach((goalType) => {
      Object.keys(validationErrors[goalType]).forEach((index) => {
        if (validationErrors[goalType][index]) {
          Object.keys(validationErrors[goalType][index]).forEach((key) => {
            if (key === 'targets') {
              const targetErrors = validationErrors[goalType][index][key];
              Object.keys(targetErrors).forEach((targetIndex) => {
                Object.keys(targetErrors[targetIndex]).forEach((targetField) => {
                  if (targetErrors[targetIndex][targetField]) {
                    errorCount++;
                  }
                });
              });
            } else {
              if (validationErrors[goalType][index][key]) {
                errorCount++;
              }
            }
          });
        }
      });
    });

    if (isBRGoalDirty) {
      errorCount += 1;
    }
    if (isPCGoalDirty) {
      errorCount += 1;
    }
    if (isSAGoalDirty) {
      errorCount += 1;
    }

    // Totally 2 active goals are required at least.
    // One active goal in skill acquisition
    // One active goal in Parent/caregiver
    if (activeSkillAcquisitionGoals.length === 0) {
      errorCount++;
    }
    if (activeCaregiverGoals.length === 0) {
      errorCount++;
    }
    dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.Goals]: errorCount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    treatmentPlan,
    validationErrors,
    activeSkillAcquisitionGoals,
    activeCaregiverGoals,
    treatmentPlans,
  ]);

  //Validate all types of goals on page load
  useEffect(() => {
    const allValidationErrors: any = {
      [GoalTypes.BehaviorReduction]: {},
      [GoalTypes.SkillAcquisition]: {},
      [GoalTypes.Caregiver]: {},
    };

    // Validate behavior reduction goals
    if (behaviorReductionGoals?.length) {
      behaviorReductionGoals.forEach((goal, index) => {
        const errors = validateGoalAndGetErrors(goal, goalVersion, TreatmentPlanResourceVersion.V1);
        allValidationErrors[GoalTypes.BehaviorReduction][index] = errors;
      });
    }

    // Validate skill acquisition goals
    if (skillAcquisitionGoals?.length) {
      skillAcquisitionGoals.forEach((goal, index) => {
        const errors = validateGoalAndGetErrors(goal, goalVersion, TreatmentPlanResourceVersion.V1);
        allValidationErrors[GoalTypes.SkillAcquisition][index] = errors;
      });
    }

    // Validate caregiver goals
    if (caregiverGoals?.length) {
      caregiverGoals.forEach((goal, index) => {
        const errors = validateGoalAndGetErrors(goal, goalVersion, TreatmentPlanResourceVersion.V1);
        allValidationErrors[GoalTypes.Caregiver][index] = errors;
      });
    }

    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: allValidationErrors,
      }),
    );
  }, [
    behaviorReductionGoals,
    skillAcquisitionGoals,
    caregiverGoals,
    goalVersion,
    treatmentPlan,
    treatmentPlans,
  ]);

  const validateGoal = (goal: ITreatmentPlanGoal, showAlerts = true) => {
    if (!goal) {
      if (showAlerts) {
        toast.error('Goal not found.');
      }
      return;
    }

    if (!goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]) {
      if (showAlerts) {
        toast.error('Focus area and short term goal fields are required.');
      }
      return;
    }

    if (!goal.date_introduced) {
      if (showAlerts) {
        toast.error('Date of introduction is required.');
      }
      return;
    }

    if (goal.baseline_at_creation === '' || goal.baseline_at_creation === null) {
      if (showAlerts) {
        toast.error('Baseline level is required.');
      }
      return;
    }

    if (goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES) {
      const masteryCriteria = goal.mastery_criteria;
      const baselineAtCreation = goal.baseline_at_creation;
      if (!masteryCriteria || !('practice_duration_unit' in masteryCriteria)) {
        if (showAlerts) {
          toast.error('Please update all fields in the Mastery Criteria section.');
        }
        return;
      }
      const errors = validateInstanceMasteryCriteria(
        masteryCriteria,
        Number(baselineAtCreation),
        TreatmentPlanResourceVersion.V1,
      );
      Object.keys(errors).forEach((key) => {
        if (errors[key]) {
          if (showAlerts) {
            toast.error(errors[key]);
          }
          return;
        }
      });
      if (Object.keys(errors).length > 0) return;
    }

    const masteryCriteria = goal?.mastery_criteria;
    const changeType =
      masteryCriteria && 'change_type' in masteryCriteria ? masteryCriteria.change_type : undefined;
    if (changeType) {
      if (!masteryCriteria || !('desired_amount' in masteryCriteria)) {
        if (showAlerts) {
          toast.error('Please update all fields in the Mastery Criteria section.');
        }
        return;
      }
      const percentageMasteryCriteria = masteryCriteria;
      if (
        changeType === MasteryCriteriaChangeTypeEnum.INCREASE &&
        !percentageMasteryCriteria.desired_amount
      ) {
        if (showAlerts) {
          toast.error('Please update all fields in the Mastery Criteria section.');
        }
        return;
      }
      if (
        changeType === MasteryCriteriaChangeTypeEnum.DECREASE &&
        percentageMasteryCriteria.desired_amount === null
      ) {
        if (showAlerts) {
          toast.error('Please update all fields in the Mastery Criteria section.');
        }
        return;
      }
      const desiredAmount = percentageMasteryCriteria?.desired_amount;
      const baselineAtCreation = goal?.baseline_at_creation;
      if (
        changeType === MasteryCriteriaChangeTypeEnum.INCREASE &&
        desiredAmount !== null &&
        desiredAmount !== undefined &&
        baselineAtCreation !== null &&
        baselineAtCreation !== undefined &&
        desiredAmount <= Number(baselineAtCreation)
      ) {
        if (showAlerts) {
          toast.error('Desired amount must be greater than baseline value.');
        }
        return;
      }

      if (
        goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION &&
        !percentageMasteryCriteria.practice_duration_count
      ) {
        if (showAlerts) {
          toast.error('Please update all fields in the Mastery Criteria section.');
        }
        return;
      }
    }

    if (
      !changeType &&
      (!('desired_amount' in (masteryCriteria ?? {})) ||
        !masteryCriteria?.desired_amount ||
        !masteryCriteria?.practice_duration_count)
    ) {
      if (showAlerts) {
        toast.error('Please update all fields in the Mastery Criteria section.');
      }
      return;
    }

    if (goal?.goal_type === GoalTypes.SkillAcquisition || goal?.goal_type === GoalTypes.Caregiver) {
      if (goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE) {
        if (goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2) {
          if (!goal.measurement_units || !('total_attempts' in goal.measurement_units)) {
            if (showAlerts) {
              toast.error('Total attempts value is required.');
            }
            return;
          }

          if (
            +goal?.baseline_at_creation >=
            +goal.measurement_units.total_attempts
          ) {
            if (showAlerts) {
              toast.error('Baseline should be less than total attempts.');
            }
            return;
          }
        }

        if (goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1) {
          if (!goal.mastery_criteria || !('total_attempts' in goal.mastery_criteria) || !goal.mastery_criteria.total_attempts) {
            if (showAlerts) {
              toast.error('Total attepmts value is required.');
            }
            return;
          }
        }
      }
    }

    if (!goal.status_at_creation) {
      if (showAlerts) {
        toast.error('Status is required.');
      }
      return;
    }

    if (goal.current_level_at_creation === '' || goal.current_level_at_creation === null) {
      if (showAlerts) {
        toast.error('Current level is required.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      goal.hypothesized_functions?.length === 0
    ) {
      if (showAlerts) {
        toast.error('Hypothesized function field is required.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] &&
      !goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]
    ) {
      if (showAlerts) {
        toast.error(
          'Associated dsm5 symptom must be provided when goal is to correct maladaptive behavior',
        );
      }
      return;
    }

    if (
      goal.goal_type !== GoalTypes.BehaviorReduction &&
      (!goal.description || goal.description?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Description requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.operational_definition ||
        goal.operational_definition?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Operational definition requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.objective || goal.objective?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(`Objective requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`);
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.antecedent_modifications ||
        goal.antecedent_modifications.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Antecedent modifications requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.consequence_modifications ||
        goal.consequence_modifications.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Consequence modifications requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (goal.targets.length === 0) {
      if (showAlerts) {
        toast.error('Goal should have at least one target.');
      }
      return;
    }

    if (goal.targets?.some((target) => target?.label?.length < GOAL_TARGET_NAME_MIN_CHARS)) {
      if (showAlerts) {
        toast.error(`Target name requires at least ${GOAL_TARGET_NAME_MIN_CHARS} characters.`);
      }
      return;
    }

    if (goal.targets?.some((target) => target?.label?.length > GOAL_TARGET_NAME_MAX_CHARS)) {
      if (showAlerts) {
        toast.error(`Target name requires maximum ${GOAL_TARGET_NAME_MAX_CHARS} characters.`);
      }
      return;
    }

    if (
      goal.targets?.some((target) => target?.instruction?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH)
    ) {
      if (showAlerts) {
        toast.error(
          `Target instruction requires minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return;
    }

    if (
      goal.targets?.some(
        (target) => target?.instruction?.length > MAXIMUM_TARGET_INSTRUCTION_LENGTH,
      )
    ) {
      if (showAlerts) {
        toast.error(
          `Target instruction has more than ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters.`,
        );
      }
      return;
    }

    if (Object.hasOwn(goal, 'id') && goal.targets?.some((target) => target.is_editing)) {
      if (showAlerts) {
        toast.error(`You must save the editing targets before save goal.`);
      }
      return;
    }

    const goalNameIds = [
      ...behaviorReductionGoals,
      ...skillAcquisitionGoals,
      ...caregiverGoals,
    ].map((goal: ITreatmentPlanGoal) => goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]);
    const isGoalDuplicate = goalNameIds?.some((id, index) => goalNameIds?.indexOf(id) !== index);
    if (isGoalDuplicate) {
      if (showAlerts) {
        toast.error('Goal with same goal name already exists.');
      }
      return;
    }

    if (goal.targets?.length > 0) {
      const labels = goal.targets?.map((target) => target?.label);
      const isTargetDuplicate = labels?.some((label, index) => labels.indexOf(label) !== index);
      if (isTargetDuplicate) {
        if (showAlerts) {
          toast.error('Target duplicated.');
        }
        return;
      }
    }

    return true;
  };

  const fetchSaveGoal = async (goal: ITreatmentPlanGoal) => {
    if (!goal || !treatmentPlan) return;

    const data = {
      ...goal,
      goal_type: type,
      status_at_creation:
        goal.status_at_creation === 'new' ? 'not_introduced' : goal.status_at_creation,
    };

    let response;
    if (Object.hasOwn(data, 'id')) {
      // update existing goal
      response = await dispatch(
        goalActions.updateGoal({
          treatmentPlanId: treatmentPlan.id,
          data,
          getAccessTokenSilently,
        }),
      );
    } else {
      // create new goal
      response = await dispatch(
        goalActions.createGoal({
          treatmentPlanId: treatmentPlan.id,
          data,
          getAccessTokenSilently,
        }),
      );
    }

    return response;
  };

  const fetchCreateTarget = async (goalId: number, target: ITreatmentPlanGoalTarget) => {
    if (!treatmentPlan || !goalId || !target) return;

    const res = await dispatch(
      goalActions.createGoalTarget({
        treatmentPlanId: treatmentPlan.id,
        goalId,
        data: target,
        getAccessTokenSilently,
      }),
    );

    if (!res || res.error) return;

    return res.payload;
  };

  const fetchUpdateTarget = async (goalId: number, target: ITreatmentPlanGoalTarget) => {
    if (!treatmentPlan || !goalId || !target) return;

    const res = await dispatch(
      goalActions.updateGoalTarget({
        treatmentPlanId: treatmentPlan.id,
        goalId,
        data: target,
        getAccessTokenSilently,
      }),
    );

    if (!res || res.error) return;

    return res.payload;
  };

  const fetchSaveTargets = async (goalId: number, targets: ITreatmentPlanGoalTarget[]) => {
    if (!goalId || !targets) return [];

    return await Promise.all(
      targets.map(async (target: ITreatmentPlanGoalTarget, index: number) => {
        let res;
        if (target.id) {
          res = await fetchUpdateTarget(goalId, target);
        } else {
          res = await fetchCreateTarget(goalId, { ...target, order: index });
        }
        return res;
      }),
    );
  };

  const handleAddNewGoal = () => {
    if (!behavior) {
      toast.error('Behavior is required to add new goal');
      return;
    }

    let newGoal = {
      ...initialTreatmentPlanGoal,
      goal_name: {
        behavior_id: '',
        label: '',
      },
      goal_name_id: '',
      goal_type: type,
    };

    if (type === GoalTypes.BehaviorReduction) {
      newGoal = {
        ...newGoal,
        operational_definition: '',
        objective: '',
        antecedent_modifications: '',
        consequence_modifications: '',
        hypothesized_functions: [],
        data_collection_method: TPGoalDataCollectionMethod.INSTANCES,
        linked_skill_acquisition_goal_ids: [],
        mastery_criteria: {
          ...DefaultInstanceMCValues(GoalTypes.BehaviorReduction, TreatmentPlanResourceVersion.V1),
        },
        measurement_units: null,
      };
    } else if (type === GoalTypes.SkillAcquisition) {
      newGoal = {
        ...newGoal,
        description: '',
        data_collection_method: TPGoalDataCollectionMethod.PERCENTAGE,
        mastery_criteria: DefaultPercentageDCMValueV1Goal(GoalTypes.SkillAcquisition),
        measurement_units: {
          ...DefaultSAGMeasurementUnitValueV1Goal,
        },
      };
    } else {
      newGoal = {
        ...newGoal,
        description: '',
        data_collection_method: TPGoalDataCollectionMethod.PERCENTAGE,
        mastery_criteria: DefaultPercentageDCMValueV1Goal(GoalTypes.Caregiver),
        measurement_units: {
          ...DefaultPCGMeasurementUnitValueV1Goal,
        },
      };
    }

    flushSync(() => {
      setGoals((prev) => [...prev, newGoal]);
    });
    goalsContainerRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' });

    updateGoalsAndErrors([...goals, newGoal]);
  };

  //function to check if the updating field is a client progress update in targets to avoid dirty state update and exclude validation
  const isTargetClientProgressUpdate = (index: number, key: string, value: any[]) => {
    if (key !== 'targets') return false;

    const oldTargets = goals[index].targets;
    return value.every((newTarget, i) => {
      const oldTarget = oldTargets[i];
      const diffKeys = Object.keys(newTarget).filter(
        (k) =>
          Object.entries(newTarget).find(([field]) => field === k)?.[1] !==
            Object.entries(oldTarget ?? {}).find(([field]) => field === k)?.[1] &&
          k !== 'client_progress_expanded' &&
          k !== 'dirty' &&
          k !== 'expanded',
      );
      return diffKeys.length === 0;
    });
  };

  const handleUpdateGoalField = (index: number, key: string, value: any) => {
    const goals = goalsRef?.current;
    if (
      key !== 'expanded' &&
      key !== 'is_active' &&
      !isTargetClientProgressUpdate(index, key, value)
    ) {
      validateAndSetErrors({ ...goals[index], [key]: value }, index, false);
    }

    const dirty = isTargetClientProgressUpdate(index, key, value)
      ? goals[index]?.['dirty'] || false // Keep existing dirty state if the field is a client progress update
      : key === 'is_active' || key === 'is_editing' || key === 'expanded' || key === 'targets'
        ? goals[index]?.['dirty'] || false
        : goals[index]?.dirty ||
          Object.entries(goals[index] ?? {}).find(([field]) => field === key)?.[1] !== value;

    if (key === GOAL_DETAILS_FIELD_IDS.GOAL_NAME) {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  [key]: value,
                  goal_name_id: '',
                  dirty,
                }
              : goal,
          ),
        }),
      );
    } else if (key === GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID) {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  [key]: value,
                  goal_name:
                    goalNames && goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id !== undefined
                      ? goalNames[goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME].behavior.id]?.find(
                          (goalName: IBehaviorGoalName) => goalName.id === value,
                        )
                      : null,
                  dirty,
                }
              : goal,
          ),
        }),
      );
    } else if (key === 'is_active') {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  [key]: value.activeStatus,
                  reason: value.reason,
                  dirty,
                  targets: goal.targets.map((target) => ({
                    ...target,
                    is_active: value.activeStatus ? true : false,
                  })),
                }
              : goal,
          ),
        }),
      );
    } else if (key === 'is_editing') {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? { ...goal, [key]: value, expanded: value ? true : goal.expanded, dirty }
              : goal,
          ),
        }),
      );
    } else if (key === GOAL_DETAILS_FIELD_IDS.LINKED_SKILL_ACQUISITION_GOALS) {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  linked_skill_acquisition_goal_ids:
                    value?.skill_acquisition?.map((goal: any) => goal.id) || [],
                  linked_goals: {
                    skill_acquisition: value?.skill_acquisition || [],
                    behavior_reduction: value?.behavior_reduction || [],
                  },
                  dirty,
                }
              : goal,
          ),
        }),
      );
    } else {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goals.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i ? { ...goal, [key]: value, dirty } : goal,
          ),
        }),
      );
    }
  };

  const validateAndSetErrors = (data: ITreatmentPlanGoal, index: number, showAlerts = true) => {
    let errors: any = {};
    const {
      dirty: _dirty,
      is_editing: _is_editing,
      is_linkable: _is_linkable,
      expanded: _expanded,
      ...rest
    } = data;
    const restWithGoalVersion = { ...rest, goal_version: goalVersion };
    Object.keys(restWithGoalVersion).forEach((key) => {
      const isValidField = validateSchema(goalSchema, key, restWithGoalVersion);
      errors = {
        ...errors,
        [key]: !isValidField,
      };
    });

    const isValid = validateGoal(data, showAlerts);
    errors['valid'] = !isValid;

    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: {
          ...validationErrors,
          [type]: {
            ...validationErrors[type],
            [index]: errors,
          },
        },
      }),
    );
    return isValid;
  };
  const handleSaveGoal = async (index: number) => {
    const goals = goalsRef.current;
    const data = { ...goals[index] };
    const isValid = validateAndSetErrors(data, index, true);
    if (!isValid) {
      setSubmittings((prev) => ({ ...prev, [index]: false }));
      return;
    }

    const { payload } = await fetchSaveGoal(data);
    if (!payload) {
      setSubmittings((prev) => ({ ...prev, [index]: false }));
      return;
    }
    const goal = {
      ...payload,
      targets: payload.targets
        ? payload.targets.sort(
            (a: ITreatmentPlanGoalTarget, b: ITreatmentPlanGoalTarget) =>
              a.order - b.order,
          )
        : [],
    };

    let targets: ITreatmentPlanGoalTarget[] = [];
    let hypothesizedFunction: IHypothesizedFunction;
    if (!data.id && data.targets.length > 0 && goal.id !== undefined) {
      const newTargets = data.targets.map((target: ITreatmentPlanGoalTarget) => ({
        ...target,
      }));
      targets = await fetchSaveTargets(goal.id, newTargets);
    }

    if (!data.id && data.hypothesized_functions && data.hypothesized_functions.length > 0) {
      const response = await dispatch(
        goalActions.createHypothesizedFunctionRecord({
          getAccessTokenSilently,
          treatmentPlanId: treatmentPlan?.id,
          goalId: goal.id,
          data: { ...data.hypothesized_functions[0], date_selected: goal.date_introduced },
        }),
      );
      if (!response.error) {
        hypothesizedFunction = response.payload;
      }
    }

    dispatch(
      goalActions.updateGoals({
        type,
        list: goals.map((prev: ITreatmentPlanGoal, i: number) =>
          index === i
            ? targets.length > 0
              ? {
                  ...goal,
                  targets,
                  dirty: false,
                  expanded: true,
                  hypothesized_functions: data.hypothesized_functions?.[0]?.id
                    ? data.hypothesized_functions.map((hf, i) => {
                        if (i === 0) {
                          return { ...hf, date_selected: goal.date_introduced };
                        }
                        return hf;
                      })
                    : hypothesizedFunction
                      ? [hypothesizedFunction]
                      : [],
                }
              : {
                  ...goal,
                  dirty: false,
                  expanded: true,
                  hypothesized_functions: data.hypothesized_functions?.[0]?.id
                    ? data.hypothesized_functions.map((hf, i) => {
                        if (i === 0) {
                          return { ...hf, date_selected: goal.date_introduced };
                        }
                        return hf;
                      })
                    : hypothesizedFunction
                      ? [hypothesizedFunction]
                      : [],
                }
            : prev,
        ),
      }),
    );

    // Update linked goal info in behavior reduction goal
    if (type === GoalTypes.SkillAcquisition) {
      updateLinkedBehaviorReductionGoals(goal);
    } else if (type === GoalTypes.BehaviorReduction) {
      updateLinkedSkillAcquisitionGoals(goal);
    }

    setSubmittings((prev) => ({ ...prev, [index]: false }));
    toast.success('Goal saved.');
  };

  const updateLinkedSkillAcquisitionGoals = (goal: ITreatmentPlanGoal) => {
    // Get the linked SA goals array
    const linkedSkillAcquisitionGoals = goal.linked_goals?.skill_acquisition || [];

    // Update all SA goals
    dispatch(
      goalActions.updateGoals({
        type: GoalTypes.SkillAcquisition,
        list: skillAcquisitionGoals.map((sa: ITreatmentPlanGoal) => {
          // Check if this SA goal is in the linked goals list
          const isLinked = linkedSkillAcquisitionGoals.some((linkedSA) => linkedSA.id === sa.id);

          if (isLinked) {
            // If linked, update/add the BR goal reference
            return {
              ...sa,
              linked_goals: {
                ...sa.linked_goals,
                behavior_reduction: [
                  ...(sa.linked_goals?.behavior_reduction?.filter((br) => br.id !== goal.id) || []),
                  { id: goal.id, goal_name: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME] },
                ],
              },
            };
          } else {
            // If not linked, remove the BR goal reference if it exists
            return {
              ...sa,
              linked_goals: {
                ...sa.linked_goals,
                behavior_reduction:
                  sa.linked_goals?.behavior_reduction?.filter((br) => br.id !== goal.id) || [],
              },
            };
          }
        }),
      }),
    );
  };

  const updateLinkedBehaviorReductionGoals = (goal: ITreatmentPlanGoal) => {
    // Update all SA linked_goals
    dispatch(
      goalActions.updateGoals({
        type: GoalTypes.BehaviorReduction,
        list: behaviorReductionGoals.map((br: ITreatmentPlanGoal) =>
          goal?.linked_goals?.behavior_reduction?.find((linkedBR) => linkedBR.id === br.id)
            ? {
                ...br,
                linked_goals: {
                  skill_acquisition:
                    br.linked_goals?.skill_acquisition?.map((sa) => {
                      if (sa.id === goal.id) {
                        return {
                          ...sa,
                          goal_name: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME] ?? {},
                        };
                      }
                      return sa;
                    }) ?? [],
                },
              }
            : br,
        ),
      }),
    );
  };

  const handleDeleteGoal = async (index: number) => {
    if (!goals[index]) {
      toast.error('Goal not found.');
      return;
    }

    setSubmittings((prev) => ({ ...prev, [index]: true }));
    if (goals[index] != null && Object.hasOwn(goals[index], 'id')) {
      // call delete api
      const res = await dispatch(
        goalActions.removeGoal({
          treatmentPlanId: treatmentPlan?.id,
          goalId: goals[index].id,
          getAccessTokenSilently,
        }),
      );

      if (!res || res.error) {
        setSubmittings((prev) => ({ ...prev, [index]: false }));
        return;
      }
      await dispatch(
        goalActions.getGoals({
          treatmentPlanId: treatmentPlan?.id,
          include_pending:
            areUserRolesIn(user?.roles, [Roles.Vbt]) &&
            !areUserRolesIn(user?.roles, [Roles.AssistantAssessor])
              ? false
              : true,
          getAccessTokenSilently,
        }),
      );
    }

    const filteredList = goals.filter((goal: ITreatmentPlanGoal, i: number) => i !== index);
    updateGoalsAndErrors(filteredList);

    setSubmittings((prev) => ({ ...prev, [index]: false }));
    toast.success('Goal deleted.');
  };

  const handleCancelGoalChange = (index: number) => {
    if (!originalGoals || !originalGoals[type]) {
      updateGoalsAndErrors(goals.filter((_, i) => index !== i));
      return;
    }

    const originalGoal = originalGoals[type]?.[index];
    const goalList =
      originalGoal && Object.hasOwn(originalGoal, 'id')
        ? goals.map((goal, i) => (index === i ? { ...originalGoal, is_editing: false } : goal))
        : goals.filter((_, i) => index !== i);
    updateGoalsAndErrors(goalList);
  };

  const updateGoalsAndErrors = (goals: ITreatmentPlanGoal[]) => {
    dispatch(
      goalActions.updateGoals({
        type,
        list: goals,
      }),
    );

    let errors: any = {};
    goals.forEach((goal, index) => {
      const { dirty: _dirty, is_editing: _is_editing, expanded: _expanded, ...rest } = goal;
      Object.keys(rest).forEach((key) => {
        const isValid = validateSchema(goalSchema, key, rest);
        errors = {
          ...errors,
          [index]: {
            ...errors[index],
            [key]: !isValid,
          },
        };
      });
    });

    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: {
          ...validationErrors,
          [type]: errors,
        },
      }),
    );
  };

  const handleSaveTarget = async (
    goalIndex: number,
    targetIndex: number,
    target: ITreatmentPlanGoalTarget,
  ) => {
    if (!goals[goalIndex]) return;

    if (!(goals[goalIndex] != null && Object.hasOwn(goals[goalIndex], 'id'))) {
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    if (target?.label?.length < 2) {
      toast.error('Target requires at least 2 characters.');
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    let savedTarget: ITreatmentPlanGoalTarget = target;
    const goalId = goals[goalIndex].id;
    if (goalId === undefined) return;
    setTargetSaving(true);
    if (Object.hasOwn(target, 'id')) {
      savedTarget = await fetchUpdateTarget(goalId, target);
    } else {
      const targetOrders = goals[goalIndex].targets
        .filter((target) => !!target.order || target.order === 0)
        .map((target) => target.order);
      const lastTargetOrder = targetOrders.length > 0 ? Math.max(...targetOrders) : 0;
      savedTarget = await fetchCreateTarget(goalId, {
        ...target,
        order: lastTargetOrder + 1,
      });
    }
    setTargetSaving(false);
    if (!savedTarget) {
      return;
    }

    updateTargetByIndex(goalIndex, targetIndex, savedTarget);
    toast.success('Target saved.');
  };

  const updateTargetByIndex = (
    goalIndex: number,
    targetIndex: number,
    target: ITreatmentPlanGoalTarget,
  ) => {
    const goalList = goals.map((prev: ITreatmentPlanGoal, i: number) =>
      goalIndex === i
        ? {
            ...prev,
            targets: prev.targets.map((t, ti) => (ti === targetIndex ? target : t)),
          }
        : prev,
    );
    dispatch(
      goalActions.updateGoals({
        type,
        list: goalList,
      }),
    );

    // Update target validation
    validateAndSetErrors(goalList[goalIndex], goalIndex, false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: '100%',
          backgroundColor: FORTA_BLUE.light3,
          borderRadius: '16px',
          p: 3,
          gap: '8px',
        }}
      >
        {goalsLoading && <FProgressBar />}
        <Box ref={goalsContainerRef}>
          {goals.map((goal: ITreatmentPlanGoal, index: number) => (
            <GoalItem
              key={index}
              index={index}
              goalVersion={goalVersion}
              values={goal}
              submitting={submittings ? submittings[index] : false}
              targetSaving={targetSaving}
              errors={
                treatmentPlan && validationErrors && validationErrors[type]
                  ? validationErrors[type][index]
                  : null
              }
              onUpdateField={(key: string, value: any) => handleUpdateGoalField(index, key, value)}
              onSave={() => {
                setSubmittings((prev) => ({ ...prev, [index]: true }));

                // Wait until update input fields on blur when click save
                setTimeout(() => {
                  handleSaveGoal(index);
                });
              }}
              onDelete={() => handleDeleteGoal(index)}
              onSaveTarget={(targetIndex: number, target: ITreatmentPlanGoalTarget) =>
                handleSaveTarget(index, targetIndex, target)
              }
              onCancelGoalChange={() => handleCancelGoalChange(index)}
            />
          ))}
        </Box>
        <FHButton
          data-testid="addGoalButton"
          startIcon={<PlusIcon color={FORTA_WHITE} />}
          colorTheme={Themes.Primary}
          sx={{ width: 'fit-content', height: '48px', justifyContent: 'flex-start' }}
          disabled={!isEditable || hasNewGoal}
          onClick={handleAddNewGoal}
        >
          Add goal
        </FHButton>
      </Box>
      {showScrollToTop && (
        //if goals accordion is expanded, show scroll to top button
        <Typography
          sx={{
            mt: 4,
            color: FORTA_GRAY4,
            textAlign: 'center',
            fontSize: '16px',
            textDecoration: 'underline',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onClick={() => {
            goalsContainerRef.current?.firstElementChild?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Scroll to top
        </Typography>
      )}
    </Box>
  );
}

export default GoalsContainer;
